import React from "react";
import classnames from "classnames";

import { IntentMapperComponentProps, IntentToClassMapping } from "./IntentMapperComponent.d";

// NOTE: invalid or missing `intent` or `type` values are not being tested
// or programatically enforced in this class; we are relying on `react` to
// provide any defaults and `TypeScript` to enforce valid usage and values
// for any classes that extend this pattern. Any `defaultProps` tests
// should be created in any extending classes, not here.
export const INTENT_MAPPER_DEFAULT_PROPS = {
    intent: "primary",
    type: "not-configured-yet",
};

const DEFAULT_CLASS_PROP = "className";

export class IntentMapperComponent<T> extends React.Component<IntentMapperComponentProps & T> {
    classNameProps = [DEFAULT_CLASS_PROP];
    static defaultProps = INTENT_MAPPER_DEFAULT_PROPS;
    intentToClasses: IntentToClassMapping<string, string> = {};

    /**
     * Returns a given property with possible additions from `intent` addons.
     *
     * @param {string} param.which - optional, which property name to use
     * @returns {string}
     *
     * For `intent` values that are not `blank`, this indexes `intentToClasses`
     * and will append the provided `which` prop value to the intent-mapped addon.
     */
    getFinalClassProp(which = DEFAULT_CLASS_PROP): string {
        if (!this.intentToClasses) {
            return "";
        }
        const { intent } = this.props;
        // replace any `intent` values that don't exist with the default
        const originalValue = this.props[which as keyof IntentMapperComponentProps & T];
        // `intent`: `blank` disables intent-mapping-to-addon functionality
        if (intent === "blank") {
            return originalValue ? String(originalValue) : "";
        }
        const intentClassAddon = this.intentToClasses[intent as string]
            ? this.intentToClasses[intent as string][which]
            : "";
        // prepend the found addons to the existing props
        return classnames(intentClassAddon, originalValue);
    }

    /**
     * Returns all `classNameProps` with their `intent`-based addons.
     *
     * @returns {Object}
     *
     * This function looks at all the keys of `classNameProps`. If the intent is
     * not `blank`, it appends the value in the props to the intent-mapped addons.
     */
    getUpdatedClassProps() {
        if (!this.classNameProps.length) {
            return {};
        }
        const newClassProps = this.classNameProps.reduce((acc, classPropName) => {
            acc[classPropName] = this.getFinalClassProp(classPropName);
            return acc;
        }, {} as Record<string, string>);
        return newClassProps;
    }

    /**
     * Returns the object's properties, excluding `intent`, and with any prop
     * keys specified in `classNameProps` updated with intent addons.
     *
     * @returns {Object}
     */
    getUpdatedProps() {
        const { intent, ...rest } = this.props;
        const actualClassNames = this.getUpdatedClassProps();
        const updatedProps = {
            ...rest,
            ...actualClassNames,
        };
        return updatedProps;
    }

    /**
     * Returns the object's properties, excluding `intent` and any prop keys
     * specified in `classNameProps`.
     *
     * @returns {Object}
     */
    getUpdatedNonClassProps() {
        const { intent, ...rest } = this.props;
        const updatedProps = Object.keys(rest).reduce((acc, key: string) => {
            if (!this.classNameProps.includes(key)) {
                acc[key] = this.props[key as keyof T];
            }
            return acc;
        }, {} as Record<string, any>);
        return updatedProps;
    }
}

export default IntentMapperComponent;
